import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  InputAdornment,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  Grid,
  Snackbar
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SidebarMenu from "./AdminPanel/SidebarMenu";
import TopBar from "./AdminPanel/TopBar";
import { useNavigate } from "react-router-dom";
import LicenseViewer from "./VariantForm/LicenseViewer";

function getCSRFToken() {
  const name = "csrftoken";
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

export default function Variant() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(false);
  const [productos, setProductos] = useState([]);
  const [busqueda, setBusqueda] = useState("");
  const [openAuto, setOpenAuto] = useState(false);
  const [producto, setProducto] = useState(null);
  const [tipoLicencia, setTipoLicencia] = useState("");
  const [licenciasExistentes, setLicenciasExistentes] = useState([]);
  const [licencias, setLicencias] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [codigo, setCodigo] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (busqueda.length < 2 || producto) return;
    (async () => {
      try {
        const resp = await fetch(`/proformas/buscar-productos/?q=${encodeURIComponent(busqueda)}`, {
          credentials: "include"
        });
        const { results = [] } = await resp.json();
        setProductos(results);
      } catch {}
    })();
  }, [busqueda, producto]);

  const handleLicensesLoaded = (type, list) => {
    setLicenciasExistentes(list);
    if (type) setTipoLicencia(type);
  };

  const onSelect = async (p) => {
    setProducto(p);
    setBusqueda(`${p.name} (${p.code})`);
    setOpenAuto(false);
    setLicencias([]);
    setLicenciasExistentes([]);
    setTipoLicencia("");
    setEmail("");
    setPassword("");
    setCodigo("");
    setReloadKey((k) => k + 1);
  };

  const clearProduct = () => {
    setProducto(null);
    setBusqueda("");
    setProductos([]);
    setLicencias([]);
    setLicenciasExistentes([]);
    setTipoLicencia("");
    setOpenAuto(false);
    setEmail("");
    setPassword("");
    setCodigo("");
    setReloadKey((k) => k + 1);
  };

  const hasEmailExisting = licenciasExistentes.some((l) => l.email);
  const hasCodeExisting = licenciasExistentes.some((l) => l.code);

  const addEmailLicense = () => {
    const hasCode = hasCodeExisting || licencias.some((l) => l.type === "code");
    if (hasCode || tipoLicencia === "code") {
      setError("Este producto solo acepta licencias de código. Elimina las licencias existentes si deseas cambiar el tipo.");
      return;
    }
    if (!email || !password) {
      setError("Completa email y contraseña.");
      return;
    }
    setTipoLicencia("email");
    setLicencias((v) => [...v, { id: Date.now(), type: "email", email, password, stock: 1 }]);
    setEmail("");
    setPassword("");
    setError("");
  };

  const addCodeLicense = () => {
    const hasEmail = hasEmailExisting || licencias.some((l) => l.type === "email");
    if (hasEmail || tipoLicencia === "email") {
      setError("Este producto solo acepta licencias de email. Elimina las licencias existentes si deseas cambiar el tipo.");
      return;
    }
    if (!codigo) {
      setError("Ingresa un código.");
      return;
    }
    setTipoLicencia("code");
    setLicencias((v) => [...v, { id: Date.now(), type: "code", code: codigo, stock: 1 }]);
    setCodigo("");
    setError("");
  };

  const deleteLicense = (id) => setLicencias((v) => v.filter((x) => x.id !== id));

  const handleTipoLicenciaChange = (e) => {
    const nuevoTipo = e.target.value;
    if (tipoLicencia && tipoLicencia !== nuevoTipo && licencias.length > 0) {
      setError("Este producto solo puede tener un tipo de licencia. Elimina las licencias agregadas para cambiar el tipo.");
      return;
    }
    setTipoLicencia(nuevoTipo);
  };

  const saveAll = async () => {
    if (!producto) {
      setError("Selecciona un producto.");
      return;
    }
    if (!licencias.length) {
      setError("Agrega licencias antes de guardar.");
      return;
    }
    if (!tipoLicencia) {
      setError("Selecciona un tipo de licencia.");
      return;
    }

    const csrf = getCSRFToken();
    const formData = new FormData();
    formData.append("product_id", producto.id);

    if (tipoLicencia === "email") {
      licencias.forEach((l) => {
        if (l.type === "email") {
          formData.append("emails[]", l.email);
          formData.append("passwords[]", l.password);
        }
      });
      try {
        const resp = await fetch("/productos/crear-licencias-email/", {
          method: "POST",
          credentials: "include",
          headers: { "X-CSRFToken": csrf },
          body: formData
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || "Error");
        setLicencias([]);
        setSuccess("Licencias creadas correctamente");
        setReloadKey((k) => k + 1);
      } catch (e) {
        setError(e.message);
      }
    }

    if (tipoLicencia === "code") {
      licencias.forEach((l) => {
        if (l.type === "code") formData.append("codes[]", l.code);
      });
      try {
        const resp = await fetch("/productos/crear-licencias-codigo/", {
          method: "POST",
          credentials: "include",
          headers: { "X-CSRFToken": csrf },
          body: formData
        });
        const data = await resp.json();
        if (!resp.ok) throw new Error(data.error || "Error");
        setLicencias([]);
        setSuccess("Licencias creadas correctamente");
        setReloadKey((k) => k + 1);
      } catch (e) {
        setError(e.message);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await fetch("/sistema/logout/", { method: "POST", credentials: "include" });
    } catch {}
    localStorage.clear();
    navigate("/login");
  };

  const handleSelect = (c) => {
    if (c === "AdminSection") navigate("/admin-panel/secciones");
    else if (c === "ModalInventario") setSubmenuOpen((s) => !s);
    else if (c === "AdminProduct") navigate("/admin-panel/inventario/productos");
    else if (c === "AdminVariant") navigate("/admin-panel/inventario/variantes");
    else if (c === "AdminProforma") navigate("/admin-panel/proformas");
    else if (c === "AdminClient") navigate("/admin-panel/clientes");
    else if (c === "AdminPromotion") navigate("/admin-panel/promociones");
    else if (c === "AdminServices") navigate("/admin-panel/servicios");
    else navigate("/admin-panel");
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f6fa" }}>
      <SidebarMenu open={open} setOpen={setOpen} submenuOpen={submenuOpen} setSubmenuOpen={setSubmenuOpen} navigate={navigate} handleLogout={handleLogout} />
      <Box sx={{ flexGrow: 1 }}>
        <TopBar open={open} setOpen={setOpen} onSelect={handleSelect} />
        <Box sx={{ width: "95%", mx: "auto", mt: 3, mb: 5 }}>
          <Button variant="contained" startIcon={<ArrowBackIcon />} sx={{ textTransform: "none", bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, mb: 3 }} onClick={() => navigate(-1)}>
            Regresar
          </Button>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
              Crear licencias de producto
            </Typography>

            <Autocomplete
              options={productos}
              getOptionLabel={(o) => `${o.name} (${o.code})`}
              inputValue={busqueda}
              open={openAuto}
              onOpen={() => busqueda.length >= 2 && setOpenAuto(true)}
              onClose={() => setOpenAuto(false)}
              onInputChange={(_, v, r) => {
                if (r === "input") {
                  setBusqueda(v);
                  setProducto(null);
                  setOpenAuto(v.length >= 2);
                  setLicenciasExistentes([]);
                  setTipoLicencia("");
                  setLicencias([]);
                  setReloadKey((k) => k + 1);
                }
              }}
              onChange={(_, v) => onSelect(v)}
              freeSolo
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Buscar producto..."
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={clearProduct}>
                          <CloseIcon />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              )}
              sx={{ mb: 3 }}
            />

            {producto && (
              <>
                <Paper sx={{ p: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Código" value={producto.code} fullWidth disabled />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Marca" value={producto.brand} fullWidth disabled />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="PVP1" value={Number(producto.pvp1).toFixed(2)} fullWidth disabled />
                    </Grid>
                  </Grid>
                </Paper>

                <LicenseViewer product={producto} reloadKey={reloadKey} onLoaded={handleLicensesLoaded} />

                <Paper sx={{ p: 2, mb: 3 }}>
                  <Typography sx={{ mb: 1, fontWeight: "bold" }}>Seleccionar tipo de licencia</Typography>
                  <Select
                    fullWidth
                    value={tipoLicencia}
                    onChange={handleTipoLicenciaChange}
                    displayEmpty
                    sx={{ width: "25%", mb: 3 }}
                    disabled={hasEmailExisting || hasCodeExisting}
                  >
                    <MenuItem value="">
                      <em>Tipo de licencia</em>
                    </MenuItem>
                    {!hasEmailExisting && <MenuItem value="email">Email</MenuItem>}
                    {!hasCodeExisting && <MenuItem value="code">Código</MenuItem>}
                  </Select>

                  {tipoLicencia === "email" && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={4}>
                        <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField label="Password" value={password} onChange={(e) => setPassword(e.target.value)} fullWidth />
                      </Grid>
                      <Grid item xs={12} sm={4} display="flex" justifyContent="center">
                        <Button variant="contained" onClick={addEmailLicense} sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, width: "50%" }}>
                          Añadir
                        </Button>
                      </Grid>
                    </Grid>
                  )}

                  {tipoLicencia === "code" && (
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <TextField label="Código" value={codigo} onChange={(e) => setCodigo(e.target.value)} fullWidth />
                      </Grid>
                      <Grid item xs={12} sm={6} display="flex" justifyContent="center">
                        <Button variant="contained" onClick={addCodeLicense} sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" }, width: "50%" }}>
                          Añadir
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                </Paper>

                {licencias.length > 0 && (
                  <Paper sx={{ p: 2, borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: "#1565C0" }}>Licencias agregadas</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#1565C0" }}>
                          <TableCell sx={{ color: "#fff" }}>Tipo</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Email</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Password</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Código</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Stock</TableCell>
                          <TableCell sx={{ color: "#fff" }}>Eliminar</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {licencias.map((l) => (
                          <TableRow key={l.id}>
                            <TableCell>{l.type === "email" ? "Email" : "Código"}</TableCell>
                            <TableCell>{l.email || "–"}</TableCell>
                            <TableCell>{l.password || "–"}</TableCell>
                            <TableCell>{l.code || "–"}</TableCell>
                            <TableCell>{l.stock}</TableCell>
                            <TableCell>
                              <IconButton onClick={() => deleteLicense(l.id)}>
                                <DeleteIcon color="error" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    <Box sx={{ textAlign: "right", mt: 2 }}>
                      <Button variant="contained" onClick={saveAll} sx={{ bgcolor: "#1565C0", "&:hover": { bgcolor: "#0D47A1" } }}>
                        Guardar licencias
                      </Button>
                    </Box>
                  </Paper>
                )}
              </>
            )}
          </Paper>
        </Box>

        <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
          <Alert severity="error">{error}</Alert>
        </Snackbar>

        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess("")} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
          <Alert severity="success">{success}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
}
